import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Languages, Phone, Crown } from "lucide-react";
import { payWithRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import { formatINR } from "@/lib/format";

export const Agents = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [feeConfig, setFeeConfig] = useState({ premium_fee_paise: 49900 });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/agents").then(({ data }) => setAgents(data));

  useEffect(() => {
    load();
    api.get("/payments/config").then(({ data }) => setFeeConfig(data));
  }, []);

  const upgrade = async () => {
    if (!user) return;
    setBusy(true);
    const res = await payWithRazorpay("premium_badge", { name: user.name, email: user.email, phone: user.phone });
    setBusy(false);
    if (res.ok) {
      toast.success("Premium badge activated for 30 days 🏆");
      // Refresh user data
      try {
        const { data } = await api.get("/auth/me");
        Object.assign(user, data);
      } catch { /* ignore */ }
      load();
    } else if (res.error !== "cancelled") {
      toast.error(`Upgrade failed: ${res.error}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="section-eyebrow">Agent Hub</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[var(--teak)] mt-2">Verified Mysuru Agents</h1>
          <p className="text-[var(--ink-muted)] mt-3 max-w-2xl">RERA-registered, multilingual, hyperlocal.</p>
        </div>
        {user?.role === "agent" && !user?.premium_active && (
          <button onClick={upgrade} disabled={busy} className="btn-primary" data-testid="agent-upgrade-btn">
            <Crown className="w-4 h-4" /> Go Premium · {formatINR(feeConfig.premium_fee_paise / 100, "buy")}/mo
          </button>
        )}
        {user?.premium_active && (
          <span className="chip-gold chip" data-testid="user-premium-badge">
            <Crown className="w-3.5 h-3.5" /> Premium Agent (active)
          </span>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((a, i) => (
          <div key={a.id} className="p-6 rounded-2xl bg-white border border-[var(--line)] fade-in-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`agent-card-${a.id}`}>
            <div className="flex items-start gap-4">
              {a.avatar ? (
                <img src={a.avatar} alt={a.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[var(--surface)] flex items-center justify-center font-serif text-xl">
                  {a.name?.charAt(0)}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-serif text-xl text-[var(--teak)]">{a.name}</h3>
                  {a.premium_active && (
                    <span className="chip-gold chip text-[10px]"><Crown className="w-3 h-3" /> Premium</span>
                  )}
                </div>
                {a.verified && (
                  <div className="flex items-center gap-1 text-xs text-[var(--emerald)] mt-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Verified · RERA {a.rera}
                  </div>
                )}
              </div>
            </div>
            {a.bio && <p className="text-sm text-[var(--ink-muted)] mt-4 leading-relaxed">{a.bio}</p>}
            {a.languages && (
              <div className="flex items-center gap-2 mt-3 text-xs text-[var(--ink-muted)]">
                <Languages className="w-3.5 h-3.5" />
                {a.languages.join(" · ")}
              </div>
            )}
            <div className="mt-5 pt-5 border-t border-[var(--line)] flex items-center justify-between">
              <div className="text-xs text-[var(--ink-muted)]">
                <span className="font-mono font-bold text-[var(--teak)]">{a.listings_count}</span> listings
              </div>
              {a.phone && (
                <a href={`tel:${a.phone}`} className="btn-ghost text-xs py-1.5 px-3" data-testid={`agent-call-${a.id}`}>
                  <Phone className="w-3.5 h-3.5" /> Call
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
